<template>
  <div>
    <page-header
      icon="mdi-account-group"
      :title="$t('users')"
      :subtitle="`${restaurantName} #${companyId}`"
      icon-color="primary"
    >
      <template #actions>
        <v-chip label color="info" small>
          <v-icon left small>mdi-account</v-icon>
          {{ employees.length }} {{ $t("users_lowercase") }}
        </v-chip>
      </template>
    </page-header>
    <!-- Employee Toolbar -->
    <employee-toolbar
      :restaurant-name="restaurantName"
      v-model="search"
      :company-children.sync="companyChildren"
      :children-companies="childrenCompanies"
      :company="company"
      :visible-employees="visibleEmployees"
      :parent-company="parentCompany"
      :show-credit-column="showCreditColumn"
      :status-filter.sync="statusFilter"
      @company-filter-changed="addUsersToParentCompany"
      @toggle-all-companies="addAllUsersToParent"
      @add-employee="dialog = true"
      @add-multiple-employees="addMultiple = true"
      @import-excel="dialogImport = true"
      @search-user="openModalSearchUser = true"
    />

    <div class="px-6 mt-2">
      <!-- Employee Table -->
      <employee-table
        :employees="filteredEmployees"
        :search.sync="search"
        :loading="loading"
        :company="company"
        :select-all-time-limit="selectAllTimeLimit"
        :show-credit-column="showCreditColumn"
        :show-office-column="companyChildren.length > 0"
        @openUserReport="openUserReport($event)"
        @current-items="visibleEmployees = $event"
        @toggle-all-time-limit="toggleAllTimeLimit"
        @user-can-see-menu="userCanSeeMenu"
        @set-can-report="setCanReport"
        @set-time-limit="setTimeLimit"
        @create-table-qr="createTableQr"
        @show-employee-qr="
          employeeQrDialogData = $event;
          employeeQrDialog = true;
        "
        @edit-item="editItem"
        @delete-item="deleteItem"
        @open-credit2="openCredit2"
      />
    </div>
    <company-employee-qr-dialog
      v-model="employeeQrDialog"
      :employee="employeeQrDialogData"
    />
    <company-menu-options-dialog
      v-model="dialogCanSeeMenu"
      :employee="menuOptionsEmployee"
      :podesavanja="podesavanja"
    />
    <company-employee-credit-dialog2
      v-if="dialogCredit2"
      v-model="dialogCredit2"
      :employee="modalCreditUser"
      :parent-companies="parentCompanies"
      @close="dialogCredit2 = false"
    />
    <company-employee-search-dialog v-model="openModalSearchUser" />
    <company-employee-quick-add-dialog
      v-model="addMultiple"
      :restaurant-name="restaurantName"
      :parent-company="parentCompany"
      :parent-companies="parentCompanies"
      :selected-company="selectedCompany"
      @employeeAdded="employeeAdded"
    />
    <company-employee-activate-qr-dialog v-model="dialogTableQr" :employee="editedItem" />

    <company-employee-dialog
      v-model="dialog"
      :employee="editedItem"
      :parent-companies="parentCompanies"
      :companies="companies"
      :selected-company="selectedCompany"
      :delivery-company="company"
      @employeeAdded="employeeAdded"
      @employeeUpdated="employeeUpdated"
      @companyUpdated="companyUpdated"
    />
    <global-confirm-box ref="confirmBox" />
    <report-employee-order-history-dialog-group
      v-if="userReport.company?.type === 'group'"
      v-model="userReport.dialog"
      :company="userReport.company"
      :employee="userReport.employee"
    />
    <report-employee-order-history-dialog-more-restaurants
      v-else-if="userReport.company?.contract === 4"
      v-model="userReport.dialog"
      :company="userReport.company"
      :employee="userReport.employee"
    />
    <report-employee-order-history-dialog
      v-else
      v-model="userReport.dialog"
      :company="userReport.company"
      :employee="userReport.employee"
    />
    <ImportExcel v-model="dialogImport" @import-completed="onImportCompleted" />
  </div>
</template>

<script>
import PageHeader from "@/components/global/PageHeader.vue";
import CompanyEmployeeQrDialog from "@/components/company/employee/dialogs/CompanyEmployeeQrDialog.vue";
import CompanyEmployeeActivateQrDialog from "@/components/company/employee/dialogs/CompanyEmployeeActivateQrDialog.vue";
import CompanyMenuOptionsDialog from "@/components/company/dialogs/CompanyMenuOptionsDialog.vue";
import CompanyEmployeeDialog from "@/components/company/employee/dialogs/CompanyEmployeeDialog.vue";
import CompanyEmployeeCreditDialog2 from "@/components/company/employee/dialogs/CompanyEmployeeCreditDialog2.vue";
import CompanyEmployeeSearchDialog from "@/components/company/employee/dialogs/CompanyEmployeeSearchDialog.vue";
import CompanyEmployeeQuickAddDialog from "@/components/company/employee/dialogs/CompanyEmployeeQuickAddDialog.vue";
import ReportEmployeeOrderHistoryDialogGroup from "@/components/report/employee/dialogs/ReportEmployeeOrderHistoryDialog.vue";
import ReportEmployeeOrderHistoryDialog from "@/components/report/employee/dialogs/ReportEmployeeMealOrderHistoryDialog.vue";
import ReportEmployeeOrderHistoryDialogMoreRestaurants from "@/components/report/more-restaurants/dialogs/ReportEmployeeOrderHistoryDialogMoreRestaurants.vue";
import EmployeeToolbar from "@/components/company/employee/EmployeeToolbar.vue";
import EmployeeTable from "@/components/company/employee/EmployeeTable.vue";
import { defaultEmployee } from "@/models/employee";
import employeeService from "@/services/employee";
import hasSnackbar from "@/mixins/hasSnackbar";
import deliveryUsersContext from "@/mixins/deliveryUsersContext";
import ImportExcel from "@/components/import-excel.vue";

export default {
  mixins: [hasSnackbar, deliveryUsersContext],
  components: {
    ImportExcel,
    CompanyEmployeeQrDialog,
    CompanyEmployeeActivateQrDialog,
    CompanyMenuOptionsDialog,
    CompanyEmployeeDialog,
    CompanyEmployeeCreditDialog2,
    CompanyEmployeeSearchDialog,
    CompanyEmployeeQuickAddDialog,
    EmployeeToolbar,
    EmployeeTable,
    ReportEmployeeOrderHistoryDialog,
    ReportEmployeeOrderHistoryDialogGroup,
    ReportEmployeeOrderHistoryDialogMoreRestaurants,
    PageHeader,
  },
  data: () => ({
    // Dialog state
    dialog: false,
    dialogTableQr: false,
    dialogImport: false,
    dialogCanSeeMenu: false,
    dialogCredit2: false,
    employeeQrDialog: false,
    addMultiple: false,
    openModalSearchUser: false,

    // UI state
    loading: false,
    search: "",
    statusFilter: "all",
    visibleEmployees: [],
    selectAllTimeLimit: 1,
    showCreditColumn: false,

    // Employee data
    employees: [],
    usersOriginal: [],
    editedItem: { ...defaultEmployee },
    menuOptionsEmployee: null,
    employeeQrDialogData: null,
    modalCreditUser: {},

    // Company data
    companies: [],
    parentCompanies: [],
    company: null,
    companyChildren: [],
    childrenCompanies: [],
    restaurantName: "",

    // Settings per employee
    podesavanja: [],
    userMenuRestaurant: [],

    userReport: {
      dialog: false,
      employee: null,
      company: null,
    },
  }),
  computed: {
    user() {
      return this.$store.state.user.user;
    },
    companyId() {
      return this.$route.params.id || this.$store.state.company.id;
    },
    selectedCompany() {
      return this.companies.find((company) => company.id === Number(this.companyId));
    },
    parentCompany() {
      return this.companies.find((company) =>
        (company.children || []).some(
          (subcompany) => subcompany.id === Number(this.companyId)
        )
      );
    },
    filteredEmployees() {
      if (this.statusFilter === "active") {
        return this.employees.filter((employee) => employee.state);
      }
      if (this.statusFilter === "inactive") {
        return this.employees.filter((employee) => !employee.state);
      }
      return this.employees;
    },
  },

  watch: {
    dialog(val) {
      if (!val) {
        this.$nextTick(() => {
          this.editedItem = Object.assign({}, defaultEmployee);
        });
      }
    },
    visibleEmployees: {
      handler() {
        // Update the master checkbox state based on visible employees
        this.updateSelectAllTimeLimitState();
      },
      deep: true,
    },
  },

  created() {
    this.initialize();
    // The parent/child company structure (B2B "office" filter) only exists for
    // regular company employees. Delivery companies live in a separate table, so
    // /companies/:id/parent-companies would 404 for them.
    if (!this.isDeliveryUsersContext) {
      this.getParentCompanies();
      this.getCompanies();
    }
  },

  methods: {
    openUserReport(item) {
      this.userReport.employee = item.employee;
      this.userReport.company = item.company;
      this.userReport.dialog = true;
    },
    editItem(item) {
      this.editedItem = Object.assign({}, item);
      this.editedItem.company_id = this.companyId;

      this.editedItem.all_cooperation =
        this.editedItem.company && this.editedItem.company.settings
          ? this.editedItem.company.settings.all_cooperation
          : "";
      if (this.companies) {
        this.companies.filter((items) => {
          if (
            items.id == item.company
              ? parseInt(item.company.id)
              : parseInt(item.company_id)
          ) {
            this.editedItem.children = items.children;
          }
        });
      }

      this.dialog = true;
    },
    employeeAdded() {
      // TEMP workaround (see backend questions 07_09_2026, point 11): create
      // endpoints don't return the new user in the list-item shape (delivery
      // even returns the whole array), so re-fetch instead of trusting the
      // payload. Once the API is fixed, drop this reload and splice the
      // returned user into `employees` directly.
      this.initialize();
    },
    employeeUpdated() {
      const index = this.employees.findIndex((item) => {
        return item.id == this.editedItem.id;
      });
      Object.assign(this.employees[index], this.editedItem);
    },
    companyUpdated() {
      this.employees.filter((item, index) => {
        if (item.id == this.editedItem.id) {
          this.employees.splice(index, 1);
        }
      });
    },
    userCanSeeMenu(item, index) {
      this.menuOptionsEmployee = item;
      this.dialogCanSeeMenu = true;
    },
    addUsersToParentCompany() {
      if (this.companyChildren.length > 0) {
        const users = [];
        this.parentCompanies.forEach((item) => {
          if (item.users && item.users.length > 0) {
            item.users.forEach((user) => {
              if (this.companyChildren.includes(user.pivot.company_id)) {
                users.push(user);
              }
            });
          }
        });
        this.employees = users;
      } else {
        this.employees = this.usersOriginal;
      }
    },
    addAllUsersToParent() {
      if (this.companyChildren.length > 0) {
        this.companyChildren = [];
        this.addUsersToParentCompany();
      } else if (this.companyChildren.length == 0) {
        let allFirmsId = [];
        this.parentCompanies.filter((firm) => {
          allFirmsId.push(firm.id);
        });
        this.companyChildren = allFirmsId;
        this.addUsersToParentCompany();
      }
    },
    getParentCompanies() {
      employeeService
        .getParentCompanies(this.companyId)
        .then(({ data }) => {
          if (!data) {
            this.parentCompanies = [];
            return;
          }
          this.parentCompanies = data.map((company) => {
            return {
              ...company,
              users: (company.users || []).map((user) => ({
                ...user,
                company,
              })),
            };
          });
        })
        .catch(() => {
          this.showErrorSnackbar(this.$t("error_loading_parent_companies"));
        });
    },
    createTableQr(user) {
      this.dialogTableQr = true;
      this.editedItem = Object.assign({}, user);
    },
    setTimeLimit(item, index) {
      employeeService
        .setTimeLimit(item)
        .then(() => {
          this.showSuccessSnackbar(this.$t("time_limit_updated"));
        })
        .catch(() => {
          this.showErrorSnackbar(this.$t("time_limit_update_failed"));
        })
        .finally(() => {
          // Update the master checkbox state after individual change
          this.$nextTick(() => {
            this.updateSelectAllTimeLimitState();
          });
        });
    },
    toggleAllTimeLimit(isSelectedAll) {
      // Get currently visible employees from the data table
      const currentVisibleEmployees = this.visibleEmployees || [];

      // Update all visible employees' time_limit based on the master checkbox
      const promises = currentVisibleEmployees
        .map((employee) => {
          if (employee.no_time_limit_user !== undefined) {
            employee.no_time_limit_user = isSelectedAll ? 1 : 0;
            // Call the API to update each employee
            return employeeService.setTimeLimit(employee).then((response) => {
              const index = this.employees.findIndex((emp) => emp.id === employee.id);
              if (index !== -1) {
                this.employees[index].no_time_limit_user =
                  response.data.data?.no_time_limit_user;
              }
            });
          }
        })
        .filter(Boolean);

      Promise.all(promises)
        .then(() => {
          this.showSuccessSnackbar(this.$t("all_time_limits_updated"));
        })
        .catch(() => {
          this.showErrorSnackbar(this.$t("time_limits_update_failed"));
        });
    },
    updateSelectAllTimeLimitState() {
      const currentVisibleEmployees = this.visibleEmployees || [];
      this.selectAllTimeLimit = currentVisibleEmployees.every(
        (emp) => emp.no_time_limit_user === 1
      )
        ? 1
        : 0;
    },
    openCredit2(item) {
      this.dialogCredit2 = true;
      this.modalCreditUser = {
        ...item,
        kredit: item.credit?.duguje || "",
      };
    },
    setCanReport(item, index, value) {
      const data = {
        settings: this.podesavanja[index],
        user: {
          ...item,
          can_report: value,
        },
      };
      employeeService
        .setCanReport(data)
        .then(() => {
          this.showSuccessSnackbar(this.$t("report_access_updated"));
          item.can_report = value;
        })
        .catch(() => {
          this.showErrorSnackbar(this.$t("report_access_update_failed"));
        });
    },
    getCompanies() {
      const companyId = Number(
        this.$route.params?.id || this.$store.state.company?.id || 0
      );

      if (this.user.type === "company") {
        employeeService
          .getParentCompanies(companyId)
          .then((res) => {
            this.childrenCompanies = res.data;
          })
          .catch(() => {
            this.showErrorSnackbar(this.$t("error_loading_companies"));
          });
      } else if (this.user.type != "admin_delivery") {
        employeeService
          .getCompanies()
          .then((res) => {
            this.companies = res.data;
            const company = this.companies.find((c) => c.id === companyId);
            this.childrenCompanies = company ? [company, ...company.children] : [];
          })
          .catch(() => {
            this.showErrorSnackbar(this.$t("error_loading_companies"));
          });
      }
    },
    initialize() {
      // Same screen serves company and restaurant employees, in a regular or a
      // delivery context. Route name picks the owner; `isDeliveryUsersContext`
      // (route meta / param) picks the endpoint.
      const companyRoutes = [
        "korisnici",
        "firma-id-korisnici",
        "firma-dostava-id-korisnici",
      ];
      const isDelivery = this.isDeliveryUsersContext;
      let fetchUsersPromise;

      if (companyRoutes.includes(this.$route.name)) {
        fetchUsersPromise = isDelivery
          ? employeeService.getCompanyUsersDelivery(this.companyId)
          : employeeService.getCompanyUsers(this.companyId);
      } else if (this.$route.name === "restoran-id-korisnici") {
        fetchUsersPromise = isDelivery
          ? employeeService.getRestaurantUsersDelivery(this.companyId)
          : employeeService.getRestaurantUsers(this.companyId);
      }

      // Fetch users and update component state
      if (fetchUsersPromise) {
        this.loading = true;
        fetchUsersPromise
          .then((res) => {
            // GET /companies/:id/users-delivery now wraps the firm in
            // { data: { ...firm, users }, status: "success" } (unified 11.09
            // with PATCH/POST — see 11_09_2026 backend answer); the regular
            // /users endpoint still returns the firm object bare. Unwrap when
            // a `data` child is present and there is no sibling `users`.
            const body =
              res.data && res.data.data && res.data.users === undefined
                ? res.data.data
                : res.data;

            this.company = body;
            this.restaurantName = body.name;
            // Delivery rows carry per-firm status on pivot.active; the global
            // user `state` can be null there. Mirror it onto `state` so the
            // existing active/inactive filter and table keep working unchanged.
            this.employees = (body.users || []).map((employee) =>
              isDelivery && employee.pivot
                ? { ...employee, state: employee.pivot.active }
                : employee
            );
            this.usersOriginal = this.employees;

            // Set flag for credit column visibility
            this.showCreditColumn = body.is_credit || false;

            // Store additional settings for each employee
            this.employees.forEach((employee, index) => {
              this.podesavanja[index] = employee.pivot?.settings;
              this.userMenuRestaurant[employee.id] = employee.pivot?.add_restoran;
            });
          })
          .catch(() => {
            this.showErrorSnackbar(this.$t("error_loading_employees"));
          })
          .finally(() => {
            this.loading = false;
            // Update master checkbox state after data is loaded
            this.$nextTick(() => {
              this.updateSelectAllTimeLimitState();
            });
          });
      }
    },
    deleteItem(item) {
      this.$refs.confirmBox
        .open(
          this.$t("delete"),
          `${this.$t("delete_confirmation")}: ${[
            this.toLatin(item.name),
            this.toLatin(item.lastname),
          ].join(" ")}`,
          {
            color: "error",
          }
        )
        .then(() => {
          // Delivery context uses its own endpoint — backend flips the pivot's
          // `active` to 0 instead of deleting the row (09_09_2026 answer, pt 4).
          const deletePromise = this.isDeliveryUsersContext
            ? employeeService.deleteUserDelivery(this.companyId, item.id)
            : employeeService.deleteUser(item.id);
          deletePromise
            .then(() => {
              const index = this.employees.findIndex(
                (employee) => employee.id === item.id
              );
              this.employees.splice(index, 1);
              this.showSuccessSnackbar(this.$t("employee_deleted"));
            })
            .catch((error) => {
              // Delivery delete answers "already removed" with 409 +
              // { message, error_code: "already_removed", status: "failed" }
              // instead of a silent 200 (09_09_2026 answer pt 4, error_code
              // added in the 11_09_2026 final answer pt 5) — map the stable
              // error_code to our own translated string instead of
              // transliterating the backend's (always Cyrillic) message.
              const errorCode = error.response?.data?.error_code;
              const message = error.response?.data?.message;
              let text;
              if (errorCode === "already_removed") {
                text = this.$t("already_removed");
              } else if (message) {
                text = this.toLatin(message);
              } else {
                text = this.$t("employee_delete_failed");
              }
              this.showErrorSnackbar(text);
            });
        })
        .catch((err) => console.warn(err));
    },
    onImportCompleted() {
      // Reload users after import is completed
      this.initialize();
    },
  },
};
</script>
